import { LegDetailsSubSection } from '@/ConsignmentDetails/LegsTab/types';
import { ConsignmentTab } from '@/ConsignmentDetails/types';
import { getEnumEntryByString } from '@/Infrastructure/Utilities';
import { getRouterInstance } from '@/Infrastructure/Routing/routerInstance';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router';

/**
 * Centralized hook for manipulating the search params regarding rendering Consignment Modal.
 * May make generic in the future but requires a deeper dive that is not needed at this time.
 */
export default function useModalParams() {
	const [searchParams] = useSearchParams();

	const form = searchParams.get('form');
	const formId = searchParams.get('formId');
	const legId = searchParams.get('legId');
	const tab = searchParams.get('tab');
	const scroll = searchParams.get('scrollTo');

	/** Memoize the search parameters and expose to consuming components in strongly typed instance */
	const modalParameters: ConsignmentDetailsSearchParameters | undefined = useMemo(() => {
		const csnTab = tab ? getEnumEntryByString(ConsignmentTab, tab) : undefined;
		const section = scroll ? getEnumEntryByString(LegDetailsSubSection, scroll) : undefined;

		return {
			// TODO: This is the correct way to handle this but Index.tsx needs types fixed to invoke correctly, time boxing for now until I can resolve types
			//form: form ? (form as ModalComponent['Component']) : undefined,
			form: form ? (form === 'consignmentDetails' ? 'consignmentDetails' : 'ediLoadTenderModal') : undefined,
			formId: Number(formId),
			legId: Number(legId),
			tab: csnTab,
			scrollTo: section
		};
	}, [form, formId, legId, tab, scroll]);

	return { modalParameters, setSearchParameters: setModalSearchParameters, clearSearchParameters: clearModalSearchParameters };
}

/**
 * Applies an edit to the current search parameters through the router instance rather than useSearchParams/useNavigate.
 * Both hooks re-render their caller on every navigation, so components that only open the modal (one per grid row)
 * would all re-render each time it opens. Reading router.state at call time needs no subscription.
 */
function updateSearchParameters(update: (params: URLSearchParams) => void) {
	const router = getRouterInstance();
	const params = new URLSearchParams(router.state.location.search);
	update(params);
	void router.navigate({ search: `?${params.toString()}` });
}

/**
 *	Adds received key value pair to the search parameters
 */
export function setModalSearchParameters(modalComponentProps?: Record<string, string>) {
	updateSearchParameters((params) => {
		if (modalComponentProps !== undefined) {
			/** Updates existing keys in place and appends new ones */
			Object.entries(modalComponentProps).forEach(([key, value]) => {
				params.set(key, value);
			});

			/** Snap scrolling is only a one time use, clear unless it was specified in this dispatch */
			if (!Object.keys(modalComponentProps).includes('scrollTo')) {
				params.delete('scrollTo');
			}
		}
	});
}

/**
 * Clears all of the search parameters related to the Consignment modal while retaining any others
 */
export function clearModalSearchParameters() {
	updateSearchParameters((params) => {
		Object.keys(ConsignmentDetailsSearchParametersKeys).forEach((key) => {
			params.delete(key);
		});
	});
}

/**
 * Schema of search parameters used by the Consignment Modal
 */
interface ConsignmentDetailsSearchParameters {
	/** Name of the modal form to render */
	form?: 'consignmentDetails' | 'ediLoadTenderModal';
	//form?: ModalComponent['Component'];
	/** Consignmnet ID, but if ever generic this should be the identifier to populate the modal */
	formId?: number;
	/** Leg ID of the currently selected leg in the Csn Det modal. If ever generic,
	 * each unique modal needs to support it's own additional properties outside
	 * of the basic id.
	 */
	legId?: number;
	tab?: ConsignmentTab;
	scrollTo?: LegDetailsSubSection;
}

/**
 * Static instance of the above type used for iterating over the keys
 */
const ConsignmentDetailsSearchParametersKeys: ConsignmentDetailsSearchParameters = {
	form: undefined,
	formId: 0,
	legId: 0,
	tab: 1,
	scrollTo: 1
};
